import { z } from 'zod';
import {
  AnalysisStatusSchema,
  AttributionModeSchema,
  AttributionSchema,
  ConnectionStateSchema,
  DesiredStateSchema,
  type ConnectionState,
  type DesiredState,
  DirectionSchema,
  EligibilitySchema,
  EpisodeOpeningSchema,
  ExclusionReasonSchema,
  GapReasonSchema,
  HistoryImportStatusSchema,
  IntentSchema,
  MediaStatusSchema,
  MessageTypeSchema,
  PrimaryMotiveSchema,
  ResponseStatusSchema,
  RoleSchema,
  TouchKindSchema,
  TranscriptStatusSchema,
} from './enums.js';
import { CriterionResultSchema, EvaluationPolicySchema, LeadClockSourceSchema } from './evaluation.js';
import { ConversationIntelligenceSchema } from './intelligence.js';
import { ConversationProjectSchema } from './projects.js';
import { DimensionEvaluationSchema } from './rubric.js';
import { BusinessScheduleSchema, HolidaySchema, MetricPolicySchema } from './settings.js';

// All timestamps on the wire are ISO-8601 UTC strings. Durations are integer seconds.
// A null metric means "unknown / not measured" and must never be rendered as zero.

const Iso = z.string();
const Id = z.string().min(1).max(64);

export const ErrorResponseSchema = z.object({
  error: z.object({
    code: z.string(),
    message: z.string(),
    details: z.unknown().optional(),
  }),
});
export type ErrorResponse = z.infer<typeof ErrorResponseSchema>;

export const OkSchema = z.object({ ok: z.literal(true) });

export function pageOf<T extends z.ZodType>(item: T) {
  return z.object({ items: z.array(item), nextCursor: z.string().nullable() });
}

// ───────────── Session / tenancy ─────────────

export const MembershipSummarySchema = z.object({
  tenantId: Id,
  tenantName: z.string(),
  tenantSlug: z.string(),
  role: RoleSchema,
  isDemo: z.boolean(),
});
export type MembershipSummary = z.infer<typeof MembershipSummarySchema>;

export const MeResponseSchema = z.object({
  user: z.object({ id: Id, email: z.string(), name: z.string(), isPlatformAdmin: z.boolean() }),
  memberships: z.array(MembershipSummarySchema),
});
export type MeResponse = z.infer<typeof MeResponseSchema>;

export const SignInRequestSchema = z.object({
  email: z.email().max(254),
  password: z.string().min(1).max(256),
});
export type SignInRequest = z.infer<typeof SignInRequestSchema>;

// ───────────── Settings ─────────────

export const TenantSettingsSchema = z.object({
  id: Id,
  name: z.string(),
  slug: z.string(),
  timezone: z.string(),
  businessSchedule: BusinessScheduleSchema,
  holidays: z.array(HolidaySchema),
  metricPolicy: MetricPolicySchema,
  metricPolicyVersion: z.number().int(),
  retentionDays: z.number().int(),
  rawEnvelopeRetentionDays: z.number().int(),
  historyImportDays: z.number().int(),
  aiEnabled: z.boolean(),
  transcriptionEnabled: z.boolean(),
  aiDailyAnalysisLimit: z.number().int(),
  aiDailyTokenBudget: z.number().int(),
  analysisDebounceMinutes: z.number().int(),
  evaluationPolicy: EvaluationPolicySchema,
  evaluationPolicyVersion: z.number().int(),
  aiMonthlyTokenAllowance: z.number().int().nullable(),
  aiMaxEvaluationsPerConversation: z.number().int(),
  aiMaxTokensPerConversation: z.number().int(),
  isDemo: z.boolean(),
  onboardedAt: Iso.nullable(),
  /** Server capability flags so the UI can explain disabled features honestly. */
  capabilities: z.object({
    aiProvider: z.enum(['anthropic', 'fake', 'disabled']),
    transcriptionProvider: z.enum(['openai', 'fake', 'disabled']),
    whatsappProvider: z.enum(['baileys', 'fake']),
  }),
});
export type TenantSettings = z.infer<typeof TenantSettingsSchema>;

export const UpdateTenantSettingsSchema = z
  .object({
    name: z.string().min(1).max(120),
    timezone: z.string().min(1).max(64),
    businessSchedule: BusinessScheduleSchema,
    metricPolicy: MetricPolicySchema,
    retentionDays: z.number().int().min(7).max(3650),
    historyImportDays: z.number().int().min(0).max(3650),
    aiEnabled: z.boolean(),
    transcriptionEnabled: z.boolean(),
    aiDailyAnalysisLimit: z.number().int().min(0).max(100000),
    aiDailyTokenBudget: z.number().int().min(0).max(100_000_000),
    analysisDebounceMinutes: z.number().int().min(1).max(24 * 60),
    evaluationPolicy: EvaluationPolicySchema,
    aiMonthlyTokenAllowance: z.number().int().min(0).max(10_000_000_000).nullable(),
    aiMaxEvaluationsPerConversation: z.number().int().min(0).max(1000),
    aiMaxTokensPerConversation: z.number().int().min(0).max(100_000_000),
    markOnboarded: z.boolean(),
  })
  .partial();
export type UpdateTenantSettings = z.infer<typeof UpdateTenantSettingsSchema>;

export const CreateHolidaySchema = HolidaySchema.omit({ id: true });
export type CreateHoliday = z.infer<typeof CreateHolidaySchema>;

// ───────────── Agents & members ─────────────

export const AgentSchema = z.object({
  id: Id,
  name: z.string(),
  active: z.boolean(),
  assignedConnectionIds: z.array(Id),
  createdAt: Iso,
});
export type Agent = z.infer<typeof AgentSchema>;

export const CreateAgentSchema = z.object({ name: z.string().trim().min(1).max(120) });
export const UpdateAgentSchema = z.object({ name: z.string().trim().min(1).max(120), active: z.boolean() }).partial();
export type CreateAgent = z.infer<typeof CreateAgentSchema>;
export type UpdateAgent = z.infer<typeof UpdateAgentSchema>;

export const MemberSchema = z.object({
  id: Id,
  userId: Id,
  email: z.string(),
  name: z.string(),
  role: RoleSchema,
  createdAt: Iso,
});
export type Member = z.infer<typeof MemberSchema>;

export const CreateMemberSchema = z.object({
  email: z.email().max(254),
  name: z.string().trim().min(1).max(120),
  role: RoleSchema,
  /** Required only when the user does not exist yet. Min 12 chars. */
  temporaryPassword: z.string().min(12).max(256).optional(),
});
export type CreateMember = z.infer<typeof CreateMemberSchema>;
export const UpdateMemberSchema = z.object({ role: RoleSchema });
export type UpdateMember = z.infer<typeof UpdateMemberSchema>;

// ───────────── Connections ─────────────

export const CoverageGapSchema = z.object({
  id: Id,
  startAt: Iso,
  endAt: Iso.nullable(),
  reason: GapReasonSchema,
});
export type CoverageGap = z.infer<typeof CoverageGapSchema>;

export const ConnectionSchema = z.object({
  id: Id,
  label: z.string(),
  displayPhone: z.string().nullable(),
  state: ConnectionStateSchema,
  stateReason: z.string().nullable(),
  desiredState: DesiredStateSchema,
  attributionMode: AttributionModeSchema,
  agentId: Id.nullable(),
  agentName: z.string().nullable(),
  lastMessageAt: Iso.nullable(),
  lastHealthyAt: Iso.nullable(),
  pairedAt: Iso.nullable(),
  history: z.object({
    status: HistoryImportStatusSchema,
    requestedFrom: Iso.nullable(),
    observedEarliest: Iso.nullable(),
    observedLatest: Iso.nullable(),
    progress: z.number().int().min(0).max(100).nullable(),
  }),
  openGapCount: z.number().int(),
  conversationCount: z.number().int(),
  /** Project used for this number's conversations unless a conversation has its own link. */
  defaultProject: z.object({ id: Id, name: z.string() }).nullable(),
  /** Active self-service pairing link (unused, unrevoked, unexpired), if any. */
  pairingLink: z.object({ createdAt: Iso, expiresAt: Iso }).nullable(),
  /** Most recent history recovery (active or finished), if any. See HistoryBackfillSchema. */
  backfill: z.lazy(() => HistoryBackfillSchema).nullable(),
  createdAt: Iso,
});
export type Connection = z.infer<typeof ConnectionSchema>;

export const ConnectionEventSchema = z.object({
  id: Id,
  type: z.string(),
  fromState: ConnectionStateSchema.nullable(),
  toState: ConnectionStateSchema.nullable(),
  detail: z.record(z.string(), z.unknown()).nullable(),
  occurredAt: Iso,
});
export type ConnectionEvent = z.infer<typeof ConnectionEventSchema>;

export const ConnectionAssignmentSchema = z.object({
  id: Id,
  mode: AttributionModeSchema,
  agentId: Id.nullable(),
  agentName: z.string().nullable(),
  effectiveFrom: Iso,
  effectiveTo: Iso.nullable(),
});

export const ConnectionDetailSchema = ConnectionSchema.extend({
  events: z.array(ConnectionEventSchema),
  gaps: z.array(CoverageGapSchema),
  assignments: z.array(ConnectionAssignmentSchema),
});
export type ConnectionDetail = z.infer<typeof ConnectionDetailSchema>;

export const CreateConnectionSchema = z.object({
  label: z.string().trim().min(1).max(80),
  attributionMode: AttributionModeSchema.default('assigned'),
  agentId: Id.nullable().optional(),
});
export type CreateConnection = z.infer<typeof CreateConnectionSchema>;

export const UpdateConnectionSchema = z
  .object({ label: z.string().trim().min(1).max(80), defaultProjectId: Id.nullable() })
  .partial()
  .refine((v) => v.label !== undefined || v.defaultProjectId !== undefined, { message: 'Nada que actualizar' });
export const AssignConnectionSchema = z.object({
  mode: AttributionModeSchema,
  agentId: Id.nullable(),
});
export type AssignConnection = z.infer<typeof AssignConnectionSchema>;
export const UnlinkConnectionSchema = z.object({
  /** Must equal the connection label: explicit, deliberate confirmation. */
  confirmLabel: z.string(),
});

/** Deleting is permanent: conversations, messages, media, evaluations and alerts of the number are removed. */
export const DeleteConnectionSchema = z.object({
  /** Must equal the connection label. */
  confirmLabel: z.string(),
});

/** A number can be deleted only when it is not enabled: paused (session stopped) or unlinked (unlink finished). */
export function canDeleteConnection(c: { desiredState: DesiredState; state: ConnectionState }): boolean {
  if (c.desiredState === 'paused') return c.state === 'paused';
  if (c.desiredState === 'unlinked') return c.state === 'disconnected' || c.state === 'logged_out';
  return false;
}

// ───────────── History backfill (recover older history for a connected number) ─────────────

export const HISTORY_BACKFILL_STATUSES = ['queued', 'running', 'completed', 'partial', 'failed', 'cancelled'] as const;
export const HistoryBackfillStatusSchema = z.enum(HISTORY_BACKFILL_STATUSES);
export type HistoryBackfillStatus = z.infer<typeof HistoryBackfillStatusSchema>;

export const HISTORY_BACKFILL_PRESET_DAYS = [7, 30, 90] as const;

export const HistoryBackfillSchema = z.object({
  id: z.string(),
  status: HistoryBackfillStatusSchema,
  requestedDays: z.number().int(),
  /** Oldest instant requested. */
  cutoffAt: z.string(),
  chatsDone: z.number().int(),
  chatsTotal: z.number().int(),
  messagesImported: z.number().int(),
  startedAt: z.string().nullable(),
  finishedAt: z.string().nullable(),
  createdAt: z.string(),
  /** e.g. 'phone_unreachable' */
  error: z.string().nullable(),
});
export type HistoryBackfill = z.infer<typeof HistoryBackfillSchema>;

export const StartHistoryBackfillSchema = z.object({
  /** Days back from now; must not exceed the business retention. */
  days: z.number().int().min(1).max(3650),
});
export type StartHistoryBackfill = z.infer<typeof StartHistoryBackfillSchema>;

// ───────────── Self-service pairing links ─────────────

export const PAIRING_LINK_DURATIONS_HOURS = [1, 24, 72] as const;

export const CreatePairingLinkSchema = z.object({
  expiresInHours: z.number().int().min(1).max(168).default(24),
});
export type CreatePairingLink = z.infer<typeof CreatePairingLinkSchema>;

/** Returned once on creation: the URL contains the secret token and is never retrievable again. */
export const CreatedPairingLinkSchema = z.object({
  url: z.string(),
  expiresAt: Iso,
});
export type CreatedPairingLink = z.infer<typeof CreatedPairingLinkSchema>;

/** Public status for the salesperson's pairing page (token in the X-Pairing-Token header). */
export const PairingStatusSchema = z.object({
  businessName: z.string(),
  connectionLabel: z.string(),
  state: ConnectionStateSchema,
  /** Paired through this link: the page shows success and never a QR again. */
  completed: z.boolean(),
  linkExpiresAt: Iso,
  /** Latest valid QR while awaiting a scan; null otherwise. */
  qr: z.object({ value: z.string(), expiresAt: Iso }).nullable(),
});
export type PairingStatus = z.infer<typeof PairingStatusSchema>;

// ───────────── Conversations ─────────────

export const ConversationListItemSchema = z.object({
  id: Id,
  contactName: z.string(),
  contactPhone: z.string().nullable(),
  connectionId: Id,
  connectionLabel: z.string(),
  eligibility: EligibilitySchema,
  exclusionReason: ExclusionReasonSchema.nullable(),
  lastMessageAt: Iso.nullable(),
  lastDirection: DirectionSchema.nullable(),
  lastMessagePreview: z.string().nullable(),
  messageCount: z.number().int(),
  currentEpisode: z
    .object({
      id: Id,
      startAt: Iso,
      isOpen: z.boolean(),
      firstResponseStatus: ResponseStatusSchema,
      firstResponseSeconds: z.number().int().nullable(),
      waitingSinceAt: Iso.nullable(),
      followUpTouches: z.number().int(),
    })
    .nullable(),
  analysis: z
    .object({
      status: AnalysisStatusSchema,
      qualityScore: z.number().int().nullable(),
      primaryMotive: PrimaryMotiveSchema.nullable(),
      intent: IntentSchema.nullable(),
      isProvisional: z.boolean(),
    })
    .nullable(),
});
export type ConversationListItem = z.infer<typeof ConversationListItemSchema>;
export const ConversationPageSchema = pageOf(ConversationListItemSchema).extend({ total: z.number().int() });
export type ConversationPage = z.infer<typeof ConversationPageSchema>;

export const ConversationListQuerySchema = z.object({
  q: z.string().max(100).optional(),
  connectionId: Id.optional(),
  agentId: Id.optional(),
  eligibility: EligibilitySchema.optional(),
  responseStatus: ResponseStatusSchema.optional(),
  from: z.string().optional(),
  to: z.string().optional(),
  sort: z.enum(['lastMessageAt', 'qualityScore', 'firstResponseSeconds']).default('lastMessageAt'),
  order: z.enum(['asc', 'desc']).default('desc'),
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(100).default(25),
});
export type ConversationListQuery = z.infer<typeof ConversationListQuerySchema>;

export const TouchSchema = z.object({
  id: Id,
  seq: z.number().int(),
  kind: TouchKindSchema,
  startAt: Iso,
  endAt: Iso,
  messageIds: z.array(Id),
  reengagement: z.boolean(),
});
export type Touch = z.infer<typeof TouchSchema>;

export const EpisodeMetricSchema = z.object({
  initiatedBy: DirectionSchema,
  attribution: AttributionSchema,
  agentId: Id.nullable(),
  agentName: z.string().nullable(),
  firstResponseStatus: ResponseStatusSchema,
  firstInboundAt: Iso.nullable(),
  firstResponseAt: Iso.nullable(),
  firstResponseSeconds: z.number().int().nullable(),
  firstResponseBusinessSeconds: z.number().int().nullable(),
  orderingAmbiguous: z.boolean(),
  uncertainReason: z.string().nullable(),
  /** LeadClock reading (null for rows computed before the lead clock existed). */
  leadStartedAt: Iso.nullable(),
  leadClockSource: LeadClockSourceSchema.nullable(),
  leadResponseStatus: ResponseStatusSchema,
  leadResponseSeconds: z.number().int().nullable(),
  inboundCount: z.number().int(),
  outboundCount: z.number().int(),
  totalBursts: z.number().int(),
  initialTouches: z.number().int(),
  followUpTouches: z.number().int(),
  activeResponseBursts: z.number().int(),
  policyVersion: z.number().int(),
  computedAt: Iso,
});
export type EpisodeMetric = z.infer<typeof EpisodeMetricSchema>;

export const AnalysisSchema = z.object({
  id: Id,
  episodeId: Id,
  status: AnalysisStatusSchema,
  isProvisional: z.boolean(),
  primaryMotive: PrimaryMotiveSchema.nullable(),
  intent: IntentSchema.nullable(),
  summary: z.string().nullable(),
  productInterest: z.string().nullable(),
  objections: z.array(z.string()),
  nextAction: z.string().nullable(),
  qualityScore: z.number().int().nullable(),
  dimensions: z.array(DimensionEvaluationSchema),
  evidenceMessageIds: z.array(Id),
  evidenceCoverage: z
    .object({
      analyzedMessages: z.number().int(),
      totalMessages: z.number().int(),
      truncated: z.boolean(),
      unavailableMedia: z.number().int(),
    })
    .nullable(),
  uncertaintyFlags: z.array(z.string()),
  strengths: z.array(z.string()),
  coaching: z.array(z.string()),
  selfReportedConfidence: z.number().nullable(),
  model: z.string().nullable(),
  provider: z.string().nullable(),
  promptVersion: z.string(),
  rubricVersion: z.string(),
  sourceRevision: z.number().int(),
  isDemo: z.boolean(),
  error: z.string().nullable(),
  createdAt: Iso,
  completedAt: Iso.nullable(),
});
export type Analysis = z.infer<typeof AnalysisSchema>;

export const EpisodeSchema = z.object({
  id: Id,
  seq: z.number().int(),
  startAt: Iso,
  lastMessageAt: Iso,
  openingDirection: DirectionSchema,
  openingReason: EpisodeOpeningSchema,
  startKnown: z.boolean(),
  isOpen: z.boolean(),
  messageCount: z.number().int(),
  metric: EpisodeMetricSchema.nullable(),
  touches: z.array(TouchSchema),
  analysis: AnalysisSchema.nullable(),
  /** Advisor criteria evaluated for this conversation (objective + AI). */
  criteria: z.array(CriterionResultSchema),
  /** Current Conversation Intelligence (AI-inferred, evidence-linked). */
  intelligence: ConversationIntelligenceSchema.nullable(),
});
export type Episode = z.infer<typeof EpisodeSchema>;

export const ConversationDetailSchema = z.object({
  id: Id,
  contactName: z.string(),
  contactPhone: z.string().nullable(),
  contactAliases: z.array(z.object({ type: z.enum(['pn', 'lid', 'other']), value: z.string() })),
  connection: z.object({ id: Id, label: z.string(), state: ConnectionStateSchema }),
  eligibility: EligibilitySchema,
  exclusionReason: ExclusionReasonSchema.nullable(),
  excludedAt: Iso.nullable(),
  revision: z.number().int(),
  metricsRevision: z.number().int(),
  messageCount: z.number().int(),
  firstMessageAt: Iso.nullable(),
  lastMessageAt: Iso.nullable(),
  episodes: z.array(EpisodeSchema),
  project: ConversationProjectSchema.nullable(),
  /** AI usage of this conversation. Only returned to platform admins (internal). */
  aiUsage: z
    .object({ evaluations: z.number().int(), failures: z.number().int(), inputTokens: z.number().int(), outputTokens: z.number().int(), costUsd: z.number(), lastEvaluatedAt: Iso.nullable() })
    .nullable(),
  coverage: z.object({
    historyStatus: HistoryImportStatusSchema,
    observedEarliest: Iso.nullable(),
    gaps: z.array(CoverageGapSchema),
  }),
});
export type ConversationDetail = z.infer<typeof ConversationDetailSchema>;

export const MessageSchema = z.object({
  id: Id,
  direction: DirectionSchema,
  sentAt: Iso,
  type: MessageTypeSchema,
  isConversational: z.boolean(),
  body: z.string().nullable(),
  isDeleted: z.boolean(),
  editedAt: Iso.nullable(),
  contentExpired: z.boolean(),
  episodeId: Id.nullable(),
  media: z
    .object({
      id: Id,
      status: MediaStatusSchema,
      mimeType: z.string().nullable(),
      durationSeconds: z.number().int().nullable(),
      sizeBytes: z.number().int().nullable(),
      transcript: z
        .object({ status: TranscriptStatusSchema, text: z.string().nullable() })
        .nullable(),
    })
    .nullable(),
});
export type Message = z.infer<typeof MessageSchema>;
export const MessagePageSchema = pageOf(MessageSchema);
export type MessagePage = z.infer<typeof MessagePageSchema>;

export const MessageListQuerySchema = z.object({
  episodeId: Id.optional(),
  cursor: z.string().optional(),
  /** Message pages are read oldest → newest within the requested window. */
  direction: z.enum(['forward', 'backward']).default('forward'),
  limit: z.coerce.number().int().min(1).max(200).default(100),
});
export type MessageListQuery = z.infer<typeof MessageListQuerySchema>;

export const ExcludeConversationSchema = z.object({ reason: ExclusionReasonSchema });
export type ExcludeConversation = z.infer<typeof ExcludeConversationSchema>;

// ───────────── Reports ─────────────

/**
 * First-response clock. "business" counts only the tenant's business hours (schedule + holidays in its
 * timezone), so a lead at 7 pm answered at 9:20 am the next business day counts 20 min. "elapsed" is real time.
 */
export const ResponseBasisSchema = z.enum(['business', 'elapsed']);
export type ResponseBasis = z.infer<typeof ResponseBasisSchema>;

export const ReportFiltersSchema = z.object({
  /** Local dates in tenant timezone, inclusive start, inclusive end day (converted to half-open [from, to+1d)). */
  from: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  to: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  connectionId: Id.optional(),
  agentId: Id.optional(),
});
export type ReportFilters = z.infer<typeof ReportFiltersSchema>;

export const ResponseAggregateSchema = z.object({
  /** Median/P90/mean over completed reliable observations only. Null when none. */
  medianSeconds: z.number().nullable(),
  p90Seconds: z.number().nullable(),
  meanSeconds: z.number().nullable(),
  medianBusinessSeconds: z.number().nullable(),
  p90BusinessSeconds: z.number().nullable(),
  meanBusinessSeconds: z.number().nullable(),
  respondedCount: z.number().int(),
  eligibleCount: z.number().int(),
  pendingCount: z.number().int(),
  uncertainCount: z.number().int(),
  /** pending / eligible (inbound-initiated); null when eligible = 0. */
  unansweredRate: z.number().nullable(),
});
export type ResponseAggregate = z.infer<typeof ResponseAggregateSchema>;

export const QualityAggregateSchema = z.object({
  averageScore: z.number().nullable(),
  scoredCount: z.number().int(),
  insufficientCount: z.number().int(),
  pendingCount: z.number().int(),
  failedCount: z.number().int(),
  disabledCount: z.number().int(),
  budgetLimitedCount: z.number().int(),
});
export type QualityAggregate = z.infer<typeof QualityAggregateSchema>;

export const FollowUpAggregateSchema = z.object({
  /** Counted by touch occurrence time (not episode cohort). */
  followUpTouches: z.number().int(),
  initialTouches: z.number().int(),
  activeResponseBursts: z.number().int(),
  episodesWithFollowUp: z.number().int(),
});

export const CoverageSummarySchema = z.object({
  connections: z.number().int(),
  connected: z.number().int(),
  disconnected: z.number().int(),
  openGaps: z.number().int(),
  partialHistory: z.number().int(),
  lastMessageAt: Iso.nullable(),
});

export const DistributionItemSchema = z.object({ key: z.string(), label: z.string(), count: z.number().int() });

export const OverviewReportSchema = z.object({
  filters: ReportFiltersSchema,
  timezone: z.string(),
  computedAt: Iso,
  isDemo: z.boolean(),
  episodes: z.object({
    total: z.number().int(),
    inboundInitiated: z.number().int(),
    outboundInitiated: z.number().int(),
  }),
  response: ResponseAggregateSchema,
  previousResponse: ResponseAggregateSchema,
  followUps: FollowUpAggregateSchema,
  previousFollowUps: FollowUpAggregateSchema,
  quality: QualityAggregateSchema,
  previousQuality: QualityAggregateSchema,
  series: z.array(
    z.object({
      date: z.string(),
      episodes: z.number().int(),
      medianResponseSeconds: z.number().nullable(),
      /** Same median counting only business hours (tenant schedule/holidays). */
      medianBusinessResponseSeconds: z.number().nullable(),
      followUps: z.number().int(),
    }),
  ),
  motives: z.array(DistributionItemSchema),
  intents: z.array(DistributionItemSchema),
  coverage: CoverageSummarySchema,
  freshness: z.object({
    oldestPendingMetricsAt: Iso.nullable(),
    pendingAnalyses: z.number().int(),
    latestAnalysisAt: Iso.nullable(),
  }),
});
export type OverviewReport = z.infer<typeof OverviewReportSchema>;

export const PerformanceQuerySchema = ReportFiltersSchema.extend({
  groupBy: z.enum(['connection', 'agent']).default('connection'),
  /** Which clock response-time sorting uses: business hours (default) or real elapsed time. */
  basis: ResponseBasisSchema.default('business'),
  sort: z
    .enum(['name', 'episodes', 'medianResponseSeconds', 'p90ResponseSeconds', 'meanResponseSeconds', 'unansweredRate', 'followUps', 'averageScore'])
    .default('episodes'),
  order: z.enum(['asc', 'desc']).default('desc'),
});
export type PerformanceQuery = z.infer<typeof PerformanceQuerySchema>;

export const PerformanceRowSchema = z.object({
  key: z.string(),
  name: z.string(),
  kind: z.enum(['connection', 'agent', 'shared', 'unassigned']),
  episodes: z.number().int(),
  response: ResponseAggregateSchema,
  followUps: z.number().int(),
  quality: QualityAggregateSchema,
  motives: z.array(DistributionItemSchema),
  hasPartialCoverage: z.boolean(),
});
export type PerformanceRow = z.infer<typeof PerformanceRowSchema>;

export const PerformanceReportSchema = z.object({
  filters: PerformanceQuerySchema,
  timezone: z.string(),
  computedAt: Iso,
  isDemo: z.boolean(),
  rows: z.array(PerformanceRowSchema),
  intents: z.array(DistributionItemSchema),
});
export type PerformanceReport = z.infer<typeof PerformanceReportSchema>;

export const ExportQuerySchema = ReportFiltersSchema.extend({
  kind: z.enum(['episodes', 'performance']).default('episodes'),
  groupBy: z.enum(['connection', 'agent']).default('connection'),
});
export type ExportQuery = z.infer<typeof ExportQuerySchema>;

// ───────────── Health ─────────────

export const ProcessingHealthSchema = z.object({
  computedAt: Iso,
  connections: z.array(
    z.object({
      id: Id,
      label: z.string(),
      state: ConnectionStateSchema,
      lastHealthyAt: Iso.nullable(),
      lastMessageAt: Iso.nullable(),
      openGaps: z.number().int(),
    }),
  ),
  outbox: z.object({ pending: z.number().int(), oldestPendingAt: Iso.nullable(), failed: z.number().int() }),
  metrics: z.object({ staleConversations: z.number().int() }),
  analysis: z.object({
    pending: z.number().int(),
    failedLast24h: z.number().int(),
    budgetLimitedToday: z.number().int(),
    usageToday: z.object({
      analyses: z.number().int(),
      inputTokens: z.number().int(),
      outputTokens: z.number().int(),
      costUsd: z.number(),
    }),
  }),
  transcription: z.object({ pending: z.number().int(), failedLast24h: z.number().int() }),
  recentFailures: z.array(z.object({ queue: z.string(), kind: z.string(), error: z.string().nullable(), at: Iso })),
});
export type ProcessingHealth = z.infer<typeof ProcessingHealthSchema>;

export const AuditEventSchema = z.object({
  id: Id,
  action: z.string(),
  objectType: z.string(),
  objectId: z.string().nullable(),
  actorEmail: z.string().nullable(),
  metadata: z.record(z.string(), z.unknown()).nullable(),
  createdAt: Iso,
});
export type AuditEvent = z.infer<typeof AuditEventSchema>;
export const AuditPageSchema = pageOf(AuditEventSchema);

// ───────────── Internal: AI usage per business (platform admins) ─────────────

export const AiUsageQuerySchema = z.object({ month: z.string().regex(/^\d{4}-\d{2}$/).optional() });
export type AiUsageQuery = z.infer<typeof AiUsageQuerySchema>;

export const AiUsageRowSchema = z.object({
  tenantId: Id,
  tenantName: z.string(),
  isDemo: z.boolean(),
  evaluations: z.number().int(),
  failedEvaluations: z.number().int(),
  conversations: z.number().int(),
  transcriptions: z.number().int(),
  audioSeconds: z.number().int(),
  inputTokens: z.number().int(),
  outputTokens: z.number().int(),
  costUsd: z.number(),
  /** Tokens per evaluated conversation (null when none). */
  tokensPerConversation: z.number().nullable(),
  monthlyTokenAllowance: z.number().int().nullable(),
  /** (input + output) / allowance; null without an allowance. */
  allowanceUsed: z.number().nullable(),
});
export type AiUsageRow = z.infer<typeof AiUsageRowSchema>;

export const AiUsageReportSchema = z.object({
  month: z.string(),
  computedAt: Iso,
  rows: z.array(AiUsageRowSchema),
  totals: AiUsageRowSchema.pick({ evaluations: true, conversations: true, inputTokens: true, outputTokens: true, costUsd: true }),
  /** Last 6 months, oldest first, all businesses. */
  months: z.array(z.object({ month: z.string(), evaluations: z.number().int(), inputTokens: z.number().int(), outputTokens: z.number().int(), costUsd: z.number() })),
});
export type AiUsageReport = z.infer<typeof AiUsageReportSchema>;

// ───────────── AI retrofit (re-evaluate with the current prompt) ─────────────

export const AiRetrofitQuerySchema = z.object({ days: z.coerce.number().int().min(1).max(3650).optional() });
export const AiRetrofitStatusSchema = z.object({
  promptVersion: z.string(),
  /** Eligible chats whose latest conversation is not evaluated with the current prompt (incl. failed). */
  pending: z.number().int(),
  /** Of those, analyses that failed or hit a budget limit and can be retried. */
  failed: z.number().int(),
  total: z.number().int(),
  /** Analyses still queued or running. */
  inProgress: z.number().int(),
  daily: z.object({ analyses: z.number().int(), analysisLimit: z.number().int(), tokens: z.number().int(), tokenBudget: z.number().int() }),
  aiEnabled: z.boolean(),
});
export type AiRetrofitStatus = z.infer<typeof AiRetrofitStatusSchema>;
export const StartAiRetrofitSchema = z.object({
  days: z.number().int().min(1).max(3650).nullable(),
  /** Max conversations to queue in this run. */
  max: z.number().int().min(1).max(5000).default(500),
});
export const StartedAiRetrofitSchema = z.object({ queued: z.number().int() });
